import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  type MotifPerte, HEURE_DEBUT_STD, HEURE_FIN_STD, MOTIF_LABEL, apercuHoraire,
} from '@/services/decomptesLocation';
import { formatDZD } from '@/utils/algeria';

export type Presence = 'PLEINE' | 'DEMI' | 'HORAIRES' | 'AUCUNE';

/** Ce que le formulaire affiche (et pré-remplit quand on corrige une journée déjà pointée). */
export interface PointageValeurs {
  presence: Presence;
  heuresSup: string;
  debut: string;
  fin: string;
  motif: MotifPerte | '';
  litres: string;
  montant: string;
}

/** Ce que le formulaire envoie au serveur. Les champs de gasoil ne sont présents que s'ils comptent. */
export interface PointageSortie {
  journees: string;
  heures_sup: string;
  heure_debut?: string;
  heure_fin?: string;
  motif_perte?: MotifPerte;
  gasoil_litres?: string;
  gasoil_avance?: string;
}

export interface GasoilConfig {
  /** Ex. « à ma charge — non déduit au loueur » : rappelle ce que fait ce gasoil. */
  libelleMode: string;
  /** Prix du litre des paramètres (DA/L) : le montant se calcule tout seul, et reste modifiable. */
  prixLitre: number;
  /** Profil sans le droit « tarifs » : il saisit des litres, le montant est calculé côté serveur. */
  masque: boolean;
}

interface Props {
  titre: string;
  enRepos: boolean;
  /** Proposer « Horaires précis » (panne, retard, fin tôt) — location entrante. */
  horaires: boolean;
  /** null = pas de gasoil à suivre (gasoil du loueur, ou location sortante). */
  gasoil: GasoilConfig | null;
  initial?: Partial<PointageValeurs>;
  /** Le jour contient déjà du gasoil : « effacer » doit alors le dire. */
  jourAvecGasoil?: boolean;
  enCours: boolean;
  onEnregistrer: (s: PointageSortie) => void;
  onEffacer: () => void;
}

const num = (s: string) => Number(s.replace(',', '.') || 0);
const inputCls = 'h-9 w-full rounded-md border border-border bg-surface-primary px-2 text-sm';

const PRESENCES: { key: Presence; label: string }[] = [
  { key: 'PLEINE', label: 'Journée pleine' },
  { key: 'DEMI', label: 'Demi-journée' },
];

/**
 * Formulaire de POINTAGE d'une journée — le même partout (grille par loueur, calendrier du
 * décompte, grille d'états). Plus de boutons « + 1 h / + 2 h » figés : on choisit la présence,
 * on saisit le nombre d'heures supplémentaires, et, si le gasoil est à suivre, les litres versés
 * (le montant en argent se calcule tout seul).
 */
export function PointageForm({ titre, enRepos, horaires, gasoil, initial, jourAvecGasoil, enCours, onEnregistrer, onEffacer }: Props) {
  const [v, setV] = useState<PointageValeurs>({
    presence: 'PLEINE', heuresSup: '', debut: HEURE_DEBUT_STD, fin: HEURE_FIN_STD, motif: '', litres: '', montant: '',
    ...initial,
  });
  // Le montant suit les litres tant qu'on ne l'a pas modifié à la main (ou s'il était déjà personnalisé).
  const [montantLibre, setMontantLibre] = useState(() => {
    const l = num(initial?.litres ?? ''), m = num(initial?.montant ?? '');
    return !!gasoil && m > 0 && Math.round(l * gasoil.prixLitre) !== Math.round(m);
  });
  const set = (patch: Partial<PointageValeurs>) => setV((p) => ({ ...p, ...patch }));

  const apercu = apercuHoraire(v.debut, v.fin);
  const presences = [
    ...PRESENCES,
    ...(horaires ? [{ key: 'HORAIRES' as Presence, label: 'Horaires précis' }] : []),
    ...(gasoil ? [{ key: 'AUCUNE' as Presence, label: 'Pas de travail (plein seul)' }] : []),
  ];

  const changerLitres = (litres: string) => {
    if (gasoil && !montantLibre) set({ litres, montant: num(litres) > 0 && gasoil.prixLitre ? String(Math.round(num(litres) * gasoil.prixLitre)) : '' });
    else set({ litres });
  };

  // Le gasoil n'est envoyé que s'il compte : saisi, ou déjà présent (pour pouvoir le corriger/vider).
  const gasoilConcerne = !!gasoil && (!!v.litres || !!v.montant || !!jourAvecGasoil || !!initial?.litres);
  const horairesInvalides = v.presence === 'HORAIRES' && (!apercu || (apercu.perdues > 0 && !v.motif));
  const pleinSeulInvalide = v.presence === 'AUCUNE' && !(num(v.litres) > 0);
  const invalide = horairesInvalides || pleinSeulInvalide;

  const enregistrer = () => {
    const sortie: PointageSortie = { journees: '1', heures_sup: '0' };
    if (v.presence === 'DEMI') sortie.journees = '0.5';
    if (v.presence === 'AUCUNE') sortie.journees = '0';
    if (v.presence === 'PLEINE' || v.presence === 'DEMI') sortie.heures_sup = v.heuresSup ? String(num(v.heuresSup)) : '0';
    if (v.presence === 'HORAIRES') {
      sortie.heure_debut = v.debut;
      sortie.heure_fin = v.fin;
      if (apercu && apercu.perdues > 0 && v.motif) sortie.motif_perte = v.motif;
    }
    if (gasoilConcerne) {
      sortie.gasoil_litres = v.litres ? String(num(v.litres)) : '0';
      if (!gasoil?.masque) sortie.gasoil_avance = v.montant ? String(num(v.montant)) : '0';
    }
    onEnregistrer(sortie);
  };

  return (
    <div className="space-y-3 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">{titre}</p>
      {enRepos && (
        <p className="rounded bg-status-stuck/10 px-2 py-1 text-[11px] text-status-stuck">Jour de repos — pointage possible (chantier urgent, heures sup).</p>
      )}

      <div>
        <p className="mb-1 text-xs font-medium text-content-secondary">Présence</p>
        <div className="grid grid-cols-2 gap-1.5">
          {presences.map((p) => (
            <button
              key={p.key} type="button" onClick={() => set({ presence: p.key })}
              className={cn('rounded-md border px-2 py-1.5 text-xs font-medium transition-colors',
                v.presence === p.key ? 'border-brand-primary bg-brand-primary/10 text-brand-primary' : 'border-border text-content-secondary hover:bg-surface-hover')}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {v.presence === 'HORAIRES' && (
        <div className="space-y-1.5 rounded-md border border-border p-2">
          <div className="grid grid-cols-2 gap-1.5">
            <label className="text-[10px] uppercase text-content-muted">Début
              <input type="time" className={cn(inputCls, 'mt-0.5')} value={v.debut} onChange={(e) => set({ debut: e.target.value })} />
            </label>
            <label className="text-[10px] uppercase text-content-muted">Fin
              <input type="time" className={cn(inputCls, 'mt-0.5')} value={v.fin} onChange={(e) => set({ fin: e.target.value })} />
            </label>
          </div>
          {apercu && apercu.perdues > 0 && (
            <select className={inputCls} value={v.motif} onChange={(e) => set({ motif: e.target.value as MotifPerte })}>
              <option value="">Motif des {apercu.perdues} h perdues…</option>
              {(Object.keys(MOTIF_LABEL) as MotifPerte[]).map((m) => <option key={m} value={m}>{MOTIF_LABEL[m]}</option>)}
            </select>
          )}
          <p className="text-[11px] text-content-secondary">
            {!apercu ? 'La fin doit suivre le début.'
              : `${apercu.perdues ? `${apercu.perdues} h perdue(s)` : 'Journée complète'}${apercu.sup ? ` · ${apercu.sup} h supplémentaire(s)` : ''}`}
          </p>
        </div>
      )}

      {(v.presence === 'PLEINE' || v.presence === 'DEMI') && (
        <label className="block text-xs font-medium text-content-secondary">
          Heures supplémentaires
          <div className="mt-1 flex items-center gap-2">
            <input type="number" min="0" step="0.5" inputMode="decimal" placeholder="0" className={inputCls}
              value={v.heuresSup} onChange={(e) => set({ heuresSup: e.target.value })} />
            <span className="text-xs text-content-muted">h</span>
          </div>
          <span className="mt-0.5 block text-[10px] font-normal text-content-muted">Laissez vide s'il n'y en a pas. Nombre d'heures au-delà de la journée normale.</span>
        </label>
      )}

      {gasoil && (
        <div className="space-y-1.5 rounded-md border border-border p-2">
          <p className="text-xs font-medium text-content-secondary">
            Gasoil mis ce jour-là <span className="font-normal text-content-muted">— {gasoil.libelleMode}</span>
          </p>
          <div className={cn('grid gap-1.5', gasoil.masque ? 'grid-cols-1' : 'grid-cols-2')}>
            <label className="text-[10px] uppercase text-content-muted">Litres
              <input type="number" min="0" step="1" inputMode="decimal" className={cn(inputCls, 'mt-0.5')}
                value={v.litres} onChange={(e) => changerLitres(e.target.value)} />
            </label>
            {!gasoil.masque && (
              <label className="text-[10px] uppercase text-content-muted">Montant (DA)
                <input type="number" min="0" step="1" inputMode="decimal" className={cn(inputCls, 'mt-0.5')}
                  value={v.montant} onChange={(e) => { setMontantLibre(true); set({ montant: e.target.value }); }} />
              </label>
            )}
          </div>
          <p className="text-[10px] text-content-muted">
            {gasoil.masque
              ? 'Saisissez les litres : leur valeur en argent est calculée et communiquée au responsable.'
              : num(v.litres) > 0 && num(v.montant) > 0
                ? `${num(v.litres)} L = ${formatDZD(num(v.montant))} (prix du litre : ${gasoil.prixLitre} DA)`
                : `Montant calculé : litres × ${gasoil.prixLitre} DA, modifiable.`}
          </p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="button" disabled={invalide || enCours} onClick={enregistrer}
          className="flex-1 rounded-md bg-brand-accent px-3 py-2 text-sm font-medium text-brand-accent-contrast disabled:opacity-40"
        >
          Enregistrer
        </button>
        <button
          type="button" disabled={enCours} onClick={onEffacer}
          className="rounded-md border border-border px-3 py-2 text-xs text-status-stuck hover:bg-status-stuck/10 disabled:opacity-40"
        >
          {jourAvecGasoil && gasoil ? 'Effacer jour + gasoil' : 'Effacer'}
        </button>
      </div>
      {invalide && (
        <p className="text-[11px] text-status-stuck">
          {pleinSeulInvalide ? 'Saisissez les litres du plein, ou choisissez une présence.' : 'Complétez les horaires et le motif.'}
        </p>
      )}
    </div>
  );
}

/** Cadre flottant positionné près de la case cliquée, avec un fond qui ferme au clic. */
export function PopoverPointage({ x, y, onClose, children }: { x: number; y: number; onClose: () => void; children: React.ReactNode }) {
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} aria-hidden />
      <div
        className="fixed z-50 max-h-[92vh] w-[300px] overflow-y-auto rounded-lg border border-border bg-surface-secondary shadow-lg"
        style={{ left: Math.max(8, Math.min(x, window.innerWidth - 312)), top: Math.max(8, Math.min(y, window.innerHeight - 560)) }}
      >
        {children}
      </div>
    </>
  );
}
